import React, { useEffect, useState, useRef } from 'react';

export const ConversationContextModal = ({ isOpen, onClose, messageId }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const referencedRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !messageId) {
            setMessages([]);
            return;
        }

        const fetchContext = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');

                const response = await fetch(`/api/SupportEngine/GetReferencedMessageContext/${messageId}`, {
                    headers: {
                        Accept: 'application/json',
                        ...(token ? { Authorization: 'Bearer ' + token } : {})
                    }
                });
                const json = await response.json();
                if (json.success && Array.isArray(json.data)) {
                    setMessages(json.data);
                } else if (Array.isArray(json)) {
                    setMessages(json);
                } else {
                    setMessages([]);
                }
            } catch (error) {
                console.error('Error al obtener contexto de la conversación:', error);
                setMessages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchContext();
    }, [isOpen, messageId]);

    // Auto-scroll al mensaje referenciado una vez que se carguen los mensajes
    useEffect(() => {
        if (!loading && messages.length > 0 && referencedRef.current) {
            referencedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [loading, messages]);

    if (!isOpen) return null;

    const formatTime = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(3px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
            onClick={onClose}
        >
            {/* Contenedor del Modal */}
            <div
                style={{
                    backgroundColor: '#efeae2',
                    width: '100%',
                    maxWidth: '680px',
                    maxHeight: '85vh',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 20px',
                        backgroundColor: '#f0f2f5',
                        borderBottom: '1px solid #e2e8f0'
                    }}
                >
                    <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                            Conversacion del mensaje
                        </h3>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                            Mensaje #{messageId}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#64748b',
                            padding: '6px 10px',
                            borderRadius: '50%'
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Cuerpo del Chat */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        backgroundImage: 'radial-gradient(#cbd5e1 0.75px, transparent 0.75px)',
                        backgroundSize: '16px 16px'
                    }}
                >
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '14px' }}>
                            Cargando mensajes de la conversación...
                        </div>
                    ) : messages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '14px' }}>
                            No se encontraron mensajes en esta conversación.
                        </div>
                    ) : (
                        messages.map((msg) => {
                            const isTarget = msg.idMensaje === messageId;
                            const isAgent = Boolean(msg.bEsAgente);

                            // Obtiene la URL del recurso multimedia (URL directa o por GetMedia con vMediaId)
                            const fileUrl = msg.vMediaUrl
                                ? msg.vMediaUrl
                                : (msg.vMediaId ? `/api/SupportEngine/GetMedia/${msg.vMediaId}` : null);

                            return (
                                <div
                                    key={msg.idMensaje}
                                    ref={isTarget ? referencedRef : null}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: isAgent ? 'flex-end' : 'flex-start',
                                        width: '100%'
                                    }}
                                >
                                    <div
                                        style={{
                                            maxWidth: '78%',
                                            borderRadius: '12px',
                                            padding: '8px 12px',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                            backgroundColor: isAgent ? '#d9fdd3' : '#ffffff',
                                            color: '#111827',
                                            fontSize: '13.5px',
                                            border: isTarget ? '2px solid #10b981' : 'none',
                                            position: 'relative'
                                        }}
                                    >
                                        {/* Badge indicador del mensaje referenciado */}
                                        {isTarget && (
                                            <div
                                                style={{
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: '#059669',
                                                    marginBottom: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                📌 Mensaje citado
                                            </div>
                                        )}

                                        {/* 📷 Imagen (Tipo 2) */}
                                        {fileUrl && msg.idCatTipoMensaje === 2 && (
                                            <img
                                                src={fileUrl}
                                                alt="Imagen"
                                                style={{
                                                    borderRadius: '8px',
                                                    maxHeight: '220px',
                                                    width: '100%',
                                                    objectFit: 'cover',
                                                    marginBottom: '6px',
                                                    display: 'block'
                                                }}
                                            />
                                        )}

                                        {/* 💟 Sticker (Tipo 3) */}
                                        {fileUrl && msg.idCatTipoMensaje === 3 && (
                                            <img
                                                src={fileUrl}
                                                alt="Sticker"
                                                style={{
                                                    width: '120px',
                                                    height: '120px',
                                                    objectFit: 'contain',
                                                    marginBottom: '6px',
                                                    display: 'block'
                                                }}
                                            />
                                        )}

                                        {/* 🎤 Nota de voz / Audio (Tipo 4) */}
                                        {fileUrl && msg.idCatTipoMensaje === 4 && (
                                            <div style={{ marginBottom: '6px' }}>
                                                <audio controls src={fileUrl} style={{ width: '100%', height: '36px' }} />
                                            </div>
                                        )}

                                        {/* 🎥 Video (Tipo 5) */}
                                        {fileUrl && msg.idCatTipoMensaje === 5 && (
                                            <video
                                                controls
                                                src={fileUrl}
                                                style={{
                                                    borderRadius: '8px',
                                                    maxHeight: '220px',
                                                    width: '100%',
                                                    marginBottom: '6px',
                                                    display: 'block'
                                                }}
                                            />
                                        )}

                                        {/* 📄 Documento (Tipo 6) */}
                                        {fileUrl && msg.idCatTipoMensaje === 6 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '10px',
                                                    padding: '6px 10px',
                                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                                    borderRadius: '8px',
                                                    marginBottom: '6px'
                                                }}
                                            >
                                                <span style={{ fontSize: '12px' }}>📄 Archivo adjunto</span>
                                                <a
                                                    href={fileUrl}
                                                    download
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        fontSize: '11.5px',
                                                        color: '#0284c7',
                                                        fontWeight: 600,
                                                        textDecoration: 'none'
                                                    }}
                                                >
                                                    Descargar
                                                </a>
                                            </div>
                                        )}

                                        {/* Texto del mensaje */}
                                        {msg.vTextoMensaje && (
                                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                                {msg.vTextoMensaje}
                                            </div>
                                        )}

                                        {/* Hora */}
                                        <div
                                            style={{
                                                fontSize: '10px',
                                                color: '#6b7280',
                                                textAlign: 'right',
                                                marginTop: '4px'
                                            }}
                                        >
                                            {formatTime(msg.dCreatedDate)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '10px 16px',
                        backgroundColor: '#f0f2f5',
                        borderTop: '1px solid #e2e8f0',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#64748b'
                    }}
                >
                    Solo lectura.
                </div>
            </div>
        </div>
    );
};
